import { useMemo } from "react";
import { useUsersMap, type TellescopeModelFromName } from "@tellescope/tanstack-query";
import type { ChatRoom } from "@tellescope/types-models";

export const collectRoomUserIds = (rooms: ChatRoom[]): string[] => {
  const ids = new Set<string>();
  for (const room of rooms) {
    if (room.recentSender) ids.add(room.recentSender);
    for (const userId of room.userIds ?? []) {
      ids.add(userId);
    }
  }
  return [...ids].sort();
};

export const selectRoomUser = (
  room: ChatRoom,
  usersById: Record<string, TellescopeModelFromName<"users">>
): TellescopeModelFromName<"users"> | undefined => {
  if (room.recentSender && usersById[room.recentSender]) return usersById[room.recentSender];
  for (const userId of room.userIds ?? []) {
    const user = usersById[userId];
    if (user) return user;
  }
  return undefined;
};

/**
 * Resolves the care-team User records referenced by the given chat rooms
 * (recent senders + room members). Ids that belong to endusers simply won't
 * match a user and are absent from the map.
 */
export const useChatRoomUsers = (rooms: ChatRoom[]) => {
  const userIds = useMemo(() => collectRoomUserIds(rooms), [rooms]);

  const usersById = useUsersMap(userIds);

  return { usersById };
};
